import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { bindFormModule } from '../../shared/formBinding.js';
import { formModalSaved, isFormModalOpen } from '../../shared/formModal.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// Creating and editing a task, from the full page and from the shared dialog
// alike - bindFormModule handles both, so nothing here knows which it is on.

// Which list API answers for each entity, and which field carries the label.
// These are the *existing* endpoints, which already scope server-side: the
// picker offers exactly what the reader may reach, without a line of new API
// surface.
const QUELLEN = {
  kunde: { pfad: '/kunden', schluessel: 'kunden', label: (row) => row.name },
  job: { pfad: '/jobs', schluessel: 'jobs', label: (row) => row.titel },
  kandidat: { pfad: '/kandidaten', schluessel: 'kandidaten', label: (row) => row.name }
};

// How long to wait after the last keystroke before searching. The same figure
// liveList.js uses, so the two feel alike.
const SUCHE_DEBOUNCE_MS = 350;

// The optional Bezug. Dropped entirely when empty, the OPTIONAL_REFS pattern:
// sending `entitaet: ''` would fail the enum, and on a PATCH an absent key is
// how "leave it alone" is expressed.
function readForm(form) {
  const data = Object.fromEntries(new FormData(form).entries());

  if (!data.entitaet || !data.entitaetId) {
    delete data.entitaet;
    delete data.entitaetId;
  }

  // Sent as '' rather than dropped when cleared: dropping it would mean
  // "unchanged" on a PATCH, so a deadline somebody removed would never go away.
  if (data.faelligAm === undefined) data.faelligAm = '';

  return data;
}

async function submit(form) {
  const submitButton = form.querySelector('button[type="submit"]');
  const { aufgabeId } = form.dataset;
  const data = readForm(form);

  if (submitButton) submitButton.disabled = true;
  showLoading('Wird gespeichert …');

  try {
    await (aufgabeId ? http.patch(`/aufgaben/${aufgabeId}`, data) : http.post('/aufgaben', data));

    // In the dialog the page behind it - the task list - is already the right
    // place, so it closes and refetches rather than navigating.
    if (isFormModalOpen()) {
      hideLoading();
      formModalSaved();
      notifySuccess(aufgabeId ? 'Aufgabe wurde aktualisiert.' : 'Aufgabe wurde erstellt.');
      return;
    }

    // From the standalone page there is nothing to refetch, so the list is where
    // the saved task is about to be read.
    window.location.href = withFlash('/', aufgabeId ? 'aufgabe-aktualisiert' : 'aufgabe-erstellt');
  } catch (err) {
    hideLoading();
    if (submitButton) submitButton.disabled = false;
    notifyError(
      err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
    );
  }
}

/**
 * The record picker: an entity select, then a <datalist> of matching records.
 *
 * <datalist> has no value/label split - the input holds the text the user sees -
 * so the id travels in a hidden field this function keeps in step. The critical
 * half is the clearing: the moment the typed text stops matching an option, the
 * hidden id is emptied, or a half-typed name would submit the previous pick's
 * id and file the task against the wrong record.
 */
function initBezugPicker(form) {
  const entitaet = form.querySelector('#entitaet');
  const suche = form.querySelector('#bezug-suche');
  const text = form.querySelector('#bezug-text');
  const optionen = form.querySelector('#bezug-optionen');
  const idFeld = form.querySelector('#entitaetId');

  if (!entitaet || !text || !optionen || !idFeld) return;

  // What the last search returned, label to id. Held here rather than read back
  // off the DOM, because an <option> carries only the label.
  let treffer = new Map();
  let timer = null;

  function syncId() {
    idFeld.value = treffer.get(text.value) || '';
  }

  async function lade() {
    const quelle = QUELLEN[entitaet.value];
    if (!quelle) return;

    try {
      const { data } = await http.get(quelle.pfad, {
        params: { q: text.value, limit: 10 }
      });

      const zeilen = data.data[quelle.schluessel] || [];
      treffer = new Map(zeilen.map((row) => [quelle.label(row), row.id]));

      optionen.innerHTML = '';
      treffer.forEach((_id, label) => {
        const option = document.createElement('option');
        option.value = label;
        optionen.appendChild(option);
      });

      // The freshly loaded options may well contain what is already typed - a
      // pick from the browser's own dropdown fires `input` before the list is
      // back.
      syncId();
    } catch {
      // A failed lookup leaves the previous options in place. The field is
      // optional, so an error toast here would interrupt somebody filling in a
      // task that does not need a Bezug at all.
    }
  }

  entitaet.addEventListener('change', () => {
    // A different entity invalidates both the text and the id: a customer's name
    // is not a job.
    text.value = '';
    idFeld.value = '';
    treffer = new Map();
    optionen.innerHTML = '';

    suche.classList.toggle('hidden', !entitaet.value);
    if (entitaet.value) lade();
  });

  text.addEventListener('input', () => {
    // Cleared immediately rather than on the debounce: between the keystroke and
    // the response the typed text no longer names the previous pick, and the
    // form must not be submittable with a stale id in that window.
    syncId();

    window.clearTimeout(timer);
    timer = window.setTimeout(lade, SUCHE_DEBOUNCE_MS);
  });

  // Editing a task that already has a Bezug: the label is rendered server-side
  // from the snapshot, so the list is loaded once to make it changeable.
  if (entitaet.value) lade();
}

export function initAufgabeForm() {
  // The third argument is not optional here: the picker has to be re-wired for
  // every form the dialog delivers, and that is the whole reason bindFormModule
  // takes a setup callback rather than leaving it to init time.
  bindFormModule('aufgabe-form', submit, initBezugPicker);
}
